import { Megaphone } from 'lucide-react';
import { useState } from 'react';

import {
  AdminSectionCard,
  EmptyState,
  InlineError,
  Switch,
  TextareaField,
} from '@/design-system';
import { AnnouncementBanner } from '@/versions/v1/chrome/AnnouncementBanner';
import { BUTTON_PRIMARY, SECTION_LABEL } from '@/shared/ui-class';
import {
  ANNOUNCEMENT_BANNER_MAX_LENGTH,
  saveAnnouncementBannerMessage,
  validateAnnouncementBannerMessage,
} from './AnnouncementBannerScene.transform';
import type { AnnouncementBannerConfig } from '@/versions/v1/shared/admin-seed';

/** Embedded in MaintenanceScene — mirrors ai-foundation AnnouncementBannerPanel. */
export function AnnouncementBannerPanel() {
  const [config, setConfig] = useState<AnnouncementBannerConfig>({
    enabled: false,
    message: '',
    updatedAt: new Date(0).toISOString(),
  });
  const [messageInput, setMessageInput] = useState('');
  const [pristine, setPristine] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleEnabled = (): void => {
    setToggleBusy(true);
    setError(null);
    setConfig((prev) => ({ ...prev, enabled: !prev.enabled }));
    setToggleBusy(false);
  };

  const saveMessage = (): void => {
    const trimmed = messageInput.trim();
    const validationError = validateAnnouncementBannerMessage(config.enabled, trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setConfig((prev) => saveAnnouncementBannerMessage(prev, trimmed));
    setPristine(true);
    setBusy(false);
  };

  const previewMessage = messageInput.trim();
  const showPreview = previewMessage.length > 0;

  return (
    <AdminSectionCard
      title="Announcement banner"
      description="Show a dismissible message to all signed-in users. Editing the message re-shows it to everyone."
      actions={
        <Switch
          id="banner-enabled"
          checked={config.enabled}
          onCheckedChange={toggleEnabled}
          disabled={toggleBusy}
          aria-label="Show announcement banner"
        />
      }
    >
      <div className="space-y-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Message</span>
          <TextareaField
            value={messageInput}
            onChange={(e) => {
              setMessageInput(e.target.value);
              setPristine(false);
              setError(null);
            }}
            placeholder="e.g. We will be performing maintenance on Saturday from 2–4 am UTC."
            rows={3}
            maxLength={ANNOUNCEMENT_BANNER_MAX_LENGTH}
          />
          <span className="text-right text-xs text-muted">
            {messageInput.length}/{ANNOUNCEMENT_BANNER_MAX_LENGTH}
          </span>
        </label>

        {error ? <InlineError>{error}</InlineError> : null}

        <button
          type="button"
          disabled={pristine || busy}
          onClick={saveMessage}
          className={BUTTON_PRIMARY}
        >
          {busy ? 'Saving…' : 'Save message'}
        </button>
      </div>

      <div className="mt-6 space-y-2">
        <p className={SECTION_LABEL}>Preview</p>
        {showPreview ? (
          <div className="overflow-hidden rounded-xl border border-[var(--glass-border)]">
            <AnnouncementBanner
              message={previewMessage}
              updatedAt={`preview-${previewMessage}`}
            />
          </div>
        ) : (
          <EmptyState
            embedded
            icon={<Megaphone className="h-8 w-8 text-zinc-400" aria-hidden />}
            title="No message"
            description="Enter a message above to preview the banner."
          />
        )}
      </div>
    </AdminSectionCard>
  );
}
